import React, { useRef, useEffect, useLayoutEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

/**
 * Canvas-based signature pad. No external dependency — vanilla 2D context
 * with mouse + touch handlers, drawn at devicePixelRatio for retina.
 *
 * Props:
 *  - onSave(dataUrl)        called with "data:image/png;base64,..." on Confirm
 *  - onClear()              optional, called when user clicks Clear
 *  - onChange(hasStrokes)   optional, fires on first stroke + on clear
 *  - width, height          canvas CSS dimensions (default 500x180)
 *  - confirmLabel           override the Confirm button label
 *  - disabled               external lock (e.g. while parent is submitting)
 *  - statusMessage          optional inline status shown above the buttons
 *  - actionsTarget          optional DOM element: the Clear / Confirm row renders THERE (a portal) — a modal's
 *                           non-scrolling footer (index.css .safe-modal-footer), so the buttons stay on screen while
 *                           the pad scrolls with the body. Until the element exists the row is not rendered.
 */
export default function SignaturePad({
  onSave,
  onClear,
  onChange,
  width = 500,
  height = 180,
  confirmLabel = 'Confirm Signature',
  disabled = false,
  statusMessage = null,
  actionsTarget,
}) {
  const canvasRef = useRef(null);
  const rootRef = useRef(null);
  // 🔑 THE CANVAS FITS ITS CONTAINER (fix/signing-scroll): its logical width is `width` or the container's width,
  // whichever is smaller — so it never overflows a phone-width popup (no sideways scroll) and is never drawn squeezed.
  // A resize that changes it (rotation, a window resize) keeps what was drawn, scaled to the new width.
  const [fitWidth, setFitWidth] = useState(width);
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const measure = () => {
      const w = Math.floor(el.clientWidth);
      setFitWidth(w > 0 ? Math.min(width, w) : width);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [width]);
  const drawing = useRef(false);
  const lastPoint = useRef(null);
  const [hasStrokes, setHasStrokes] = useState(false);

  // Configure the canvas for retina + draw the baseline guide. Re-runs when
  // the canvas mounts or dimensions change.
  const hasStrokesRef = useRef(false);
  const setup = useCallback((keep = false) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // The drawing so far, when the pad is only being resized (not cleared).
    let prev = null;
    if (keep && hasStrokesRef.current && canvas.width && canvas.height) {
      prev = document.createElement('canvas');
      prev.width = canvas.width; prev.height = canvas.height;
      prev.getContext('2d').drawImage(canvas, 0, 0);
    }
    const dpr = window.devicePixelRatio || 1;
    canvas.width = fitWidth * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${fitWidth}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, fitWidth, height);
    if (prev) {
      ctx.drawImage(prev, 0, 0, fitWidth, height);   // a resize: the drawing (and its guide line) kept, rescaled
    } else {
      // Guide line at ~70% down — gives users something to sign on top of.
      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(20, height * 0.7);
      ctx.lineTo(fitWidth - 20, height * 0.7);
      ctx.stroke();
    }
    // Stroke style for the user's signature
    ctx.strokeStyle = '#111827';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, [fitWidth, height]);

  useEffect(() => { setup(true); }, [setup]);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches && e.touches[0];
    const clientX = t ? t.clientX : e.clientX;
    const clientY = t ? t.clientY : e.clientY;
    // 🔑 IN THE CANVAS'S OWN COORDINATES (fp/safe-area): the context is scaled for `width` × `height` CSS px, but
    // maxWidth: 100% can draw the canvas narrower than `width` (a phone, a padded modal body, a landscape inset), and
    // then a raw client offset lands the stroke to the left of the finger. Scale by drawn size → logical size.
    const sx = rect.width ? fitWidth / rect.width : 1;
    const sy = rect.height ? height / rect.height : 1;
    return { x: (clientX - rect.left) * sx, y: (clientY - rect.top) * sy };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    drawing.current = true;
    lastPoint.current = getPoint(e);
  };

  const move = (e) => {
    if (!drawing.current || disabled) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const p = getPoint(e);
    ctx.beginPath();
    ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPoint.current = p;
    if (!hasStrokes) {
      setHasStrokes(true);
      hasStrokesRef.current = true;
      if (onChange) onChange(true);
    }
  };

  const end = () => { drawing.current = false; lastPoint.current = null; };

  const handleClear = () => {
    setup();
    setHasStrokes(false);
    hasStrokesRef.current = false;
    if (onChange) onChange(false);
    if (onClear) onClear();
  };

  const handleConfirm = () => {
    if (!hasStrokes || disabled) return;
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    if (onSave) onSave(dataUrl);
  };

  const buttonBase = {
    padding: '10px 18px',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: 700,
    cursor: 'pointer',
    border: 'none',
    transition: 'opacity 0.15s',
  };
  const clearBtn = {
    ...buttonBase,
    background: '#ffffff',
    color: '#374151',
    border: '1px solid #d1d5db',
  };
  const confirmBtn = {
    ...buttonBase,
    background: '#3a6ad9',
    color: '#ffffff',
    opacity: hasStrokes && !disabled ? 1 : 0.5,
    cursor: hasStrokes && !disabled ? 'pointer' : 'not-allowed',
  };

  const actions = (
    <div style={{ display: 'flex', gap: '10px', marginTop: actionsTarget === undefined ? '4px' : 0 }}>
      <button type="button" onClick={handleClear} style={clearBtn} disabled={disabled}>
        Clear
      </button>
      <button type="button" onClick={handleConfirm} style={confirmBtn} disabled={!hasStrokes || disabled}>
        {confirmLabel}
      </button>
    </div>
  );

  return (
    <div ref={rootRef} style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0, maxWidth: '100%' }}>
      <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: 500 }}>Sign below</div>
      <canvas
        ref={canvasRef}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
        style={{
          border: '1px solid #e5e7eb',
          borderRadius: '10px',
          background: '#ffffff',
          touchAction: 'none',
          display: 'block',
          maxWidth: '100%',
        }}
      />
      {statusMessage && (
        <div style={{ fontSize: '13px', color: '#6b7280' }}>{statusMessage}</div>
      )}
      {actionsTarget === undefined && actions}
      {actionsTarget ? createPortal(actions, actionsTarget) : null}
    </div>
  );
}
